'use client';

import { useState, useId, useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import styles from './tooltip.module.scss';

export type TooltipProps = {
  /** The tooltip content text */
  content: ReactNode;
  /** Optional custom ID for the tooltip */
  id?: string;
  /** Icon color - defaults to brand primary */
  color?: string;
  /** Optional border for the tooltip bubble, e.g. '1px solid white' */
  border?: string;
};

/**
 * A help icon that displays a tooltip on hover.
 * Styled to match the legacy Paction tooltip component.
 */
export function Tooltip({ content, id, color, border }: TooltipProps) {
  const generatedId = useId();
  const tooltipId = id ?? `tooltip-${generatedId}`;
  const [canPortal, setCanPortal] = useState(false);

  useEffect(() => {
    // Ensure document is available (avoid SSR issues) then allow portal
    if (typeof document !== 'undefined') setCanPortal(true);
  }, []);

  return (
    <>
      <svg
        className={styles.icon}
        data-tooltip-id={tooltipId}
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Help"
        role="img"
      >
        <path
          d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 17H9V15H11V17ZM13.07 9.25L12.17 10.17C11.45 10.9 11 11.5 11 13H9V12.5C9 11.4 9.45 10.4 10.17 9.67L11.41 8.41C11.78 8.05 12 7.55 12 7C12 5.9 11.1 5 10 5C8.9 5 8 5.9 8 7H6C6 4.79 7.79 3 10 3C12.21 3 14 4.79 14 7C14 7.88 13.64 8.68 13.07 9.25Z"
          fill={color ?? 'var(--color-brand-primary-500)'}
        />
      </svg>
      {canPortal &&
        createPortal(
          <ReactTooltip
            id={tooltipId}
            className={styles.tooltip}
            place="top"
            border={border}
            style={{
              backgroundColor: 'var(--color-brand-primary-500)',
              color: '#fff',
              zIndex: 100000,
              maxWidth: '20rem',
              fontSize: 'var(--font-size-sm)',
              padding: 'var(--space-sm) var(--space-md)',
              borderRadius: 'var(--radius-md)'
            }}
          >
            {content}
          </ReactTooltip>,
          document.body
        )}
    </>
  );
}
